/**
 * OpenRTAR2Service — plazas de alojamiento del Registro de Turismo de
 * Andalucía (OpenRTA) por municipio, desde R2 (`ALBRTA-a`, 1-oct-2026).
 *
 * Ruta R2: /openrta/{ine5}.json.gz (la escribe `scripts/download-openrta.mjs`,
 * cada mes desde `.github/workflows/update-openrta.yml`).
 * 404 = el municipio no tiene ningún registro de alojamiento.
 * Licencia: CC BY 4.0 (ver `ATRIBUCION_OPENRTA`).
 *
 * Lo consumen P3 (`ALBRTA-b1`, `consultarMunicipio`) y P2 + exportación (`ALBRTA-b2`).
 *
 * @module services/geocoding/specialized
 */

import type { OpenRTAMunicipio } from '../../../types/openrta';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

const FETCH_TIMEOUT = 15_000;

/** Resultado de consultar un municipio (`ALBRTA-b1`, DOP-6 a). */
export type ConsultaOpenRTA =
  | { estado: 'con_registro'; datos: OpenRTAMunicipio }
  | { estado: 'sin_registro' }
  | { estado: 'fallo' };
const CACHE_MAX_ITEMS = 50;

let instancia: OpenRTAR2Service | null = null;

export function getOpenRTAR2Service(): OpenRTAR2Service {
  if (!instancia) instancia = new OpenRTAR2Service();
  return instancia;
}

/** Reinicia el singleton (tests). */
export function resetOpenRTAR2Service(): void {
  instancia?.clearCache();
  instancia = null;
}

export class OpenRTAR2Service {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, OpenRTAMunicipio | null>();
  private readonly pendientes = new Map<string, Promise<ConsultaOpenRTA>>();

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE;
  }

  /**
   * Fichero del municipio, o null si no tiene registros (404) o si falla la
   * carga (en ese caso no se cachea, para reintentar en la siguiente llamada).
   */
  async loadMunicipio(codINE: string): Promise<OpenRTAMunicipio | null> {
    const consulta = await this.consultarMunicipio(codINE);
    return consulta.estado === 'con_registro' ? consulta.datos : null;
  }

  /**
   * ALBRTA-b1 (DOP-6 a): como `loadMunicipio`, pero distingue «el municipio no tiene
   * ningún registro» (404, se cachea) de «no se pudo consultar» (no se cachea).
   */
  async consultarMunicipio(codINE: string): Promise<ConsultaOpenRTA> {
    if (!/^\d{5}$/.test(codINE)) return { estado: 'fallo' };
    if (this.cache.has(codINE)) {
      const datos = this.cache.get(codINE);
      return datos ? { estado: 'con_registro', datos } : { estado: 'sin_registro' };
    }
    const enCurso = this.pendientes.get(codINE);
    if (enCurso) return enCurso;

    const promesa = this.descarga(codINE);
    this.pendientes.set(codINE, promesa);
    try {
      return await promesa;
    } finally {
      this.pendientes.delete(codINE);
    }
  }

  clearCache(): void {
    this.cache.clear();
  }

  private async descarga(codINE: string): Promise<ConsultaOpenRTA> {
    const url = `${this.baseUrl}/openrta/${codINE}.json.gz`;
    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });
      if (response.status === 404) {
        this.guarda(codINE, null);
        return { estado: 'sin_registro' };
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const datos = (await response.json()) as OpenRTAMunicipio;
      if (datos?.ine !== codINE || !Array.isArray(datos.alojamientos)) {
        throw new Error('fichero sin la forma esperada');
      }
      this.guarda(codINE, datos);
      return { estado: 'con_registro', datos };
    } catch (error) {
      console.warn(`[OPENRTA_R2] ${codINE}: no se pudo cargar (${(error as Error).message})`);
      return { estado: 'fallo' };
    }
  }

  private guarda(codINE: string, datos: OpenRTAMunicipio | null): void {
    if (this.cache.size >= CACHE_MAX_ITEMS) {
      const primera = this.cache.keys().next().value;
      if (primera !== undefined) this.cache.delete(primera);
    }
    this.cache.set(codINE, datos);
  }
}
